import { useEffect, useId, useState } from 'react';
import moment from 'moment-timezone';
import { DateField } from '@/components/DateField';
import { OptionCombobox } from '@/components/OptionCombobox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useApi } from '@/lib/legacy';
import { useEventCallback } from '../ui/hooks';

export type AhsPackage = { id: string; name: string };
export type AhsPolicy = {
  id: number | string;
  policyNumber?: string;
  netPremium?: number;
  coveredValue?: number;
  effectiveUntil?: string | number;
  packageId?: string;
  timezone?: string;
};

type Errors = { policyNumber?: string; netPremium?: string; coveredValue?: string; packageId?: string; submit?: string; validationError?: boolean };

const numberOf = (text: string) => parseFloat(text) || 0;

function Field({
  label,
  value,
  onChange,
  error,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  type?: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="text"
        inputMode={type === 'number' ? 'decimal' : undefined}
        value={value}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Renews an Auto Hail-Safe policy: its number, premium, covered value, package and the new end
 * date (a year on by default, at the old end's time of day in the policy's timezone).
 */
export function AhsRenewalDialog({
  policy,
  open,
  packages,
  inferredPackageId,
  onClose,
  onSuccess,
}: {
  policy: AhsPolicy;
  open: boolean;
  packages: AhsPackage[];
  inferredPackageId: string | null;
  onClose: () => void;
  onSuccess: (renewed: { id: number | string }) => void;
}) {
  const [policyNumber, setPolicyNumber] = useState('');
  const [packageId, setPackageId] = useState<string | null>(null);
  const [netPremium, setNetPremium] = useState('0');
  const [coveredValue, setCoveredValue] = useState('0');
  const [errors, setErrors] = useState<Errors>({});
  const { renewAhsPolicy } = useApi();
  const [loading, setLoading] = useState(false);

  const policyTz = policy?.timezone || 'America/Chicago';

  // The original time of day, kept when the user picks a new date.
  const [originalTime, setOriginalTime] = useState({ hour: 0, minute: 0, second: 0 });
  const [end, setEnd] = useState(() =>
    policy?.effectiveUntil ? moment(policy.effectiveUntil).tz(policyTz).add(1, 'year') : moment().tz(policyTz).add(1, 'year')
  );

  const resetData = useEventCallback(() => {
    setErrors({});
    setPolicyNumber(policy?.policyNumber || '');
    setNetPremium(String(policy?.netPremium || 0));
    setCoveredValue(String(policy?.coveredValue || 0));
    setPackageId(policy?.packageId || inferredPackageId || '');
    const oldEnd = policy?.effectiveUntil ? moment(policy.effectiveUntil).tz(policyTz) : moment().tz(policyTz);
    setOriginalTime({ hour: oldEnd.hour(), minute: oldEnd.minute(), second: oldEnd.second() });
    setEnd(oldEnd.clone().add(1, 'year'));
  });

  useEffect(() => {
    if (open) {
      resetData();
    }
  }, [open, resetData]);

  // DateField works in local calendar dates; the policy's end is an instant in its own timezone.
  const endAsCalendarDate = new Date(end.year(), end.month(), end.date());
  const changeEnd = (date: Date | null) => {
    if (!date) return;
    const withTime = moment(date).tz(policyTz).hour(originalTime.hour).minute(originalTime.minute).second(originalTime.second);
    setEnd(withTime);
  };

  const validate = () => {
    const found: Errors = { validationError: true };
    let valid = true;
    if (!policyNumber.length) {
      found.policyNumber = 'Required';
      valid = false;
    }
    if (numberOf(netPremium) === 0) {
      found.netPremium = 'Required';
      valid = false;
    }
    if (numberOf(coveredValue) === 0) {
      found.coveredValue = 'Required';
      valid = false;
    }
    if (!packageId) {
      found.packageId = 'Select a package';
      valid = false;
    }
    if (!valid) {
      throw found;
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      setErrors({});
      validate();
      const payload = {
        sourcePolicyNumber: policyNumber,
        netPremium: numberOf(netPremium),
        coveredValue: numberOf(coveredValue),
        packageId,
        effectiveAt: policy?.effectiveUntil || Date.now(),
        effectiveUntil: end.valueOf(),
        boundAt: Date.now(),
      };
      const renewedPolicy = await renewAhsPolicy(policy.id, payload);
      if (renewedPolicy) {
        onSuccess(renewedPolicy);
        return;
      }
      setLoading(false);
    } catch (e) {
      const error = e as Errors;
      setErrors(error && error.validationError ? error : { submit: 'Failed to renew AHS policy' });
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[640px] md:max-w-[920px]">
        <DialogHeader>
          <DialogTitle>Renew AHS Policy</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Field label="Source Policy Number" value={policyNumber} onChange={setPolicyNumber} error={errors.policyNumber} />
          <Field label="Net Premium" type="number" value={netPremium} onChange={setNetPremium} error={errors.netPremium} />
          <Field label="Covered Value" type="number" value={coveredValue} onChange={setCoveredValue} error={errors.coveredValue} />
          <div className="flex flex-col gap-1">
            <DateField label="Effective End Date" value={endAsCalendarDate} onChange={changeEnd} />
            <p className="text-xs text-muted-foreground">{end.clone().tz(policyTz).format('MM/DD/YYYY hh:mm A z')}</p>
          </div>
          <OptionCombobox
            label="Package"
            placeholder="-- Select a package --"
            options={packages}
            value={packages.find((p) => p.id === packageId) ?? null}
            onChange={(option) => setPackageId(option && option.id ? option.id : null)}
            getLabel={(option) => option.name}
            isEqual={(option, value) => option.id === value.id}
            error={errors.packageId}
          />
          {errors.submit ? <p className="text-sm text-destructive">{errors.submit}</p> : null}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading}>
            {loading ? <Spinner /> : null}
            Renew Policy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
